import { useCallback, useState } from 'react';
import { NOTES_SAVED_ERROR, NOTES_SAVED_SUCCESS, NOTES_SHOULD_NOT_BE_EMPTY } from 'common/constants/services/notes';
import { NoteData, OnAddNoteParams } from 'components/Notes/types';
import { getArrayValue } from 'utilities';
import { useResponse } from '../useResponse';
import { useSaveNotes } from './useSaveNotes';
import { getPersistableNotes, hasNoteContent, onAddNote, onDeleteNote, onUpdateNotes } from './utilities';

interface SaveNotesOptions {
  onSuccess?: (savedNotes: NoteData[]) => void;
  onError?: () => void;
  onSettled?: () => void;
}

export const useNotes = () => {
  const [notes, setNotes] = useState<NoteData[] | null>(null);
  const [notesHasChanged, setNotesHasChanged] = useState<boolean>(false);
  const { mutateAsync: saveNotesFetch } = useSaveNotes();
  const { infoNotification, errorNotification, successNotification } = useResponse();

  const getParsedNotes = useCallback(
    (notesData: NoteData[]) => {
      const notesToSave = notesData ?? notes;
      if (notesToSave && notesToSave?.length > 0) {
        return notesToSave.map(({ temp_id, isNew, author, updated_by, ...rest }) => rest);
      }
      return [];
    },
    [notes]
  );

  // Validate only notes that would be persisted as active edits. Soft-deleted rows and
  // empty auto-seeded drafts must not block saving a delete-only change.
  const allNotesHaveContent = (notesData: NoteData[]) =>
    getPersistableNotes(notesData).every(note => note.is_deleted || hasNoteContent(note.content));

  const saveNotes = useCallback(
    async (notesData?: NoteData[], options?: SaveNotesOptions) => {
      const notesToSave = notesData ?? notes;
      if (!notesToSave) {
        return;
      }

      const areAllNotesValidContent = allNotesHaveContent(getArrayValue(notesToSave));
      if (!areAllNotesValidContent) {
        infoNotification(NOTES_SHOULD_NOT_BE_EMPTY);
        return;
      }

      // Strip phantom empty drafts before POST; keep is_deleted rows for backend deletion.
      const parsedNotes = getParsedNotes(getPersistableNotes(getArrayValue(notesToSave)));
      if (parsedNotes.length === 0) {
        setNotesHasChanged(false);
        options?.onSuccess?.([]);
        options?.onSettled?.();
        return;
      }

      try {
        const savedNotes = await saveNotesFetch({ notes: parsedNotes });
        successNotification(NOTES_SAVED_SUCCESS);
        setNotesHasChanged(false);

        // Replace temporary drafts with their persisted ids so another note can be added immediately.
        if (notesData === undefined) {
          setNotes(savedNotes);
        }

        options?.onSuccess?.(savedNotes);
        return savedNotes;
      } catch {
        errorNotification(NOTES_SAVED_ERROR);
        options?.onError?.();
      } finally {
        options?.onSettled?.();
      }
    },
    [notes, getParsedNotes, saveNotesFetch, successNotification, errorNotification, infoNotification]
  );

  const handleOnAddNote = useCallback(
    ({ pageType, pageTypeId, is_internal }: OnAddNoteParams) => {
      onAddNote({
        notes: notes ?? [],
        setNotes,
        pageType,
        pageTypeId,
        isInternal: is_internal,
      });
      setNotesHasChanged(true);
    },
    [notes, setNotes]
  );

  const handleOnUpdateNotes = useCallback(
    (updatedNote: NoteData) => {
      onUpdateNotes({
        notes: notes ?? [],
        setNotes,
        updatedNote,
      });
      setNotesHasChanged(true);
    },
    [notes, setNotes]
  );

  const handleOnDeleteNote = useCallback(
    (updatedNote: NoteData) => {
      onDeleteNote({
        notes: notes ?? [],
        setNotes,
        updatedNote,
      });
      setNotesHasChanged(true);
    },
    [notes, setNotes]
  );

  return {
    notes,
    setNotes,
    notesHasChanged,
    setNotesHasChanged,
    saveNotes,
    onAddNote: handleOnAddNote,
    onUpdateNotes: handleOnUpdateNotes,
    onDeleteNote: handleOnDeleteNote,
  };
};

export default useNotes;
